// src/services/entityService.js
import api from '../utils/api';

const ENTITY_BASE_URL = '/entities';

const entityService = {
  // Get all entities with pagination, filtering, and search
  getEntities: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search
      if (params.search) queryParams.append('search', params.search);
      
      // Add filters
      if (params.isOpen !== undefined) queryParams.append('isOpen', params.isOpen);
      if (params.minRating) queryParams.append('minRating', params.minRating);
      if (params.city) queryParams.append('city', params.city);
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${ENTITY_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single entity by ID
  getEntityById: async (id) => {
    try {
      const response = await api.get(`${ENTITY_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new entity
  createEntity: async (entityData) => {
    try {
      const response = await api.post(ENTITY_BASE_URL, entityData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update entity
  updateEntity: async (id, entityData) => {
    try {
      const response = await api.put(`${ENTITY_BASE_URL}/${id}`, entityData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Partial update entity
  patchEntity: async (id, entityData) => {
    try {
      const response = await api.patch(`${ENTITY_BASE_URL}/${id}`, entityData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete entity
  deleteEntity: async (id) => {
    try {
      const response = await api.delete(`${ENTITY_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get entity products
  getEntityProducts: async (entityId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(
        `${ENTITY_BASE_URL}/${entityId}/products?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get entity agencies
  getEntityAgencies: async (entityId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.lat && params.long) {
        queryParams.append('lat', params.lat);
        queryParams.append('long', params.long);
      }
      
      const response = await api.get(
        `/agencies/entity/${entityId}?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Toggle entity status
  toggleEntityStatus: async (id) => {
    try {
      const response = await api.patch(`${ENTITY_BASE_URL}/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload entity image
  uploadEntityImage: async (id, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      
      const response = await api.post(
        `${ENTITY_BASE_URL}/${id}/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk delete entities
  bulkDeleteEntities: async (ids) => {
    try {
      const response = await api.delete(`${ENTITY_BASE_URL}/bulk`, { data: { ids } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Export entities
  exportEntities: async (format = 'csv', filters = {}) => {
    try {
      const response = await api.get(`${ENTITY_BASE_URL}/export/${format}`, {
        params: filters,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get entity statistics
  getEntityStats: async () => {
    try {
      const response = await api.get(`${ENTITY_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get nearby entities
  getNearbyEntities: async (lat, long, radius = 10) => {
    try {
      const response = await api.get(
        `${ENTITY_BASE_URL}/nearby?lat=${lat}&long=${long}&radius=${radius}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default entityService;